import { ServiceAttendanceComponent } from '../../components/services/service-attendance-component.js';
import { ServiceModifierComponent } from '../../components/services/service-modifier-component.js';
import { ServicePriceComponent } from '../../components/services/service-price-component.js';
import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { ArgumentException } from '../../exceptions.js';
import { CalendarOrderBase } from '../../models/calendar-order-base.js';
import { CalendarOrderItem } from '../../models/serializable/calendar-order-item.js';
import { Currency } from '../../models/serializable/currency.js';
import { OnlineCalendarOrder } from '../../models/serializable/online-calendar-order.js';
import { PhoneCalendarOrder } from '../../models/serializable/phone-calendar-order.js';
import { Price } from '../../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { LegacyOrderDiscountBreakdown } from './discount-breakdown.js';
import { LegacyOrderFeeBreakdown } from './fee-breakdown.js';
import { BasicOrderPriceBreakdown, IOrderPriceBreakdown } from './order-price-breakdown.js';
import { IPaymentHelperAdapter } from './payment-helper-adapter.js';

export abstract class CalendarOrderPaymentHelper extends IPaymentHelperAdapter {
  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof CalendarOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is CalendarOrderPaymentHelper {
    return dm instanceof CalendarOrderPaymentHelper;
  }

  getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return order.services.length > 0 ?
      order.services[0].service.getPrice().from.currency:
      null;
  }

  calculateDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceBreakdownForOnlineOrder(order: CustomerOrderBase): IOrderPriceBreakdown | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const itemPrice = this.calculatePriceForOnlineCalendarOrder(order);
    if(itemPrice == null) {
      return null;
    }

    return BasicOrderPriceBreakdown.calendar({
      donationPrice: null,
      itemPrice,
      standardFeePrice: null,
      totalFeePrice: null,
      totalPrice: itemPrice.clone(),
    });
  }

  calculateDiscountBreakdownForOrder(order: CustomerOrderBase): LegacyOrderDiscountBreakdown | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateFeeBreakdownForOrder(order: CustomerOrderBase): LegacyOrderFeeBreakdown | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    let orderPrice = Price.zero(currency);

    for(const orderItem of order.services) {
      const itemPrice = this.calculatePriceForCalendarOrderItem(orderItem);
      if(isNullOrUndefined(itemPrice) || itemPrice.currency !== currency) {
        return null;
      }

      orderPrice = orderPrice.add(itemPrice);
    }

    return orderPrice;
  }

  calculateCheckoutPriceForDiscountProvider(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineCalendarOrder(order);
  }

  calculateIncomeRangeForOrder(order: CustomerOrderBase): { from: Price, to: Price } | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceRangeForOnlineCalendarOrder(order);
  }

  calculateIncomeForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineCalendarOrder(order);
  }

  calculateIncomeWithoutDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineCalendarOrder(order);
  }

  canCreateRefundForOnlineOrder(order: CustomerOrderBase): boolean | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateExistingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateRemainingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateMaxRefundForOnlineOrder(order: CustomerOrderBase): Price | null {
    if(!CalendarOrderBase.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  private calculatePriceRangeForOnlineCalendarOrder(order: CalendarOrderBase): { from: Price, to: Price } | null {
    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    let orderPriceFrom = Price.zero(currency);
    let orderPriceTo = Price.zero(currency);

    for(const orderItem of order.services) {
      const itemPrice = this.calculatePriceRangeForCalendarOrderItem(orderItem);
      if(isNullOrUndefined(itemPrice) || itemPrice.from.currency !== currency || itemPrice.to.currency !== currency) {
        return null;
      }

      orderPriceFrom = orderPriceFrom.add(itemPrice.from);
      orderPriceTo = orderPriceTo.add(itemPrice.to);
    }

    return { from: orderPriceFrom, to: orderPriceTo };
  }

  private calculatePriceForOnlineCalendarOrder(order: CalendarOrderBase): Price | null {
    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    let orderPrice = Price.zero(currency);

    for(const orderItem of order.services) {
      const itemPrice = this.calculatePriceForCalendarOrderItem(orderItem);
      if(isNullOrUndefined(itemPrice) || itemPrice.currency !== currency) {
        return null;
      }

      orderPrice = orderPrice.add(itemPrice);
    }

    return orderPrice;
  }

  calculatePriceRangeForCalendarOrderItem(orderItem: CalendarOrderItem): { from: Price, to: Price} | null {
    const currency = orderItem.service.currency;

    let serviceFrom = Price.zero(currency);
    let serviceTo = Price.zero(currency);

    const priceComponent = orderItem.service.components.findComponentByTypeId<ServicePriceComponent>(ServicePriceComponent.TYPE_ID);
    if(isNotNullOrUndefined(priceComponent)) {
      const priceRange = priceComponent.priceRanges.get(currency);
      if(isNullOrUndefined(priceRange)) return null;

      serviceFrom = serviceFrom.add(priceRange.from);
      serviceTo = serviceTo.add(priceRange.to);
    }

    const attendanceComponent = orderItem.service.components.findComponentByTypeId<ServiceAttendanceComponent>(ServiceAttendanceComponent.TYPE_ID);
    if(isNotNullOrUndefined(attendanceComponent)) {
      for(const group of attendanceComponent.groups) {
        const priceRange = group.priceRanges.get(currency);
        if(isNullOrUndefined(priceRange)) return null;

        const scaledPriceRange = priceRange.scale(group.groupSize);
        serviceFrom = serviceFrom.add(scaledPriceRange.from);
        serviceTo = serviceTo.add(scaledPriceRange.to);
      }
    }

    const modifierComponents = orderItem.service.components.findComponentsByTypeId<ServiceModifierComponent>(ServiceModifierComponent.TYPE_ID);
    for(const modifierComponent of modifierComponents) {
      for(const modifier of modifierComponent.modifiers) {
        if(modifier.selected) {
          const priceRange = modifier.priceRanges.get(currency);
          if(isNullOrUndefined(priceRange)) return null;

          serviceFrom = serviceFrom.add(priceRange.from);
          serviceTo = serviceTo.add(priceRange.to);
        }
      }
    }

    return { from: serviceFrom, to: serviceTo };
  }

  calculatePriceForCalendarOrderItem(orderItem: CalendarOrderItem): Price | null {
    const priceRange = this.calculatePriceRangeForCalendarOrderItem(orderItem);
    if(priceRange == null) return priceRange;

    return priceRange.from.value === priceRange.to.value ? priceRange.to : null;
  }
}

export class OnlineCalendarOrderPaymentHelper extends CalendarOrderPaymentHelper {
  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof OnlineCalendarOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is OnlineCalendarOrderPaymentHelper {
    return dm instanceof OnlineCalendarOrderPaymentHelper;
  }
  
  public readonly orderTypeId: string = OnlineCalendarOrder.TYPE_ID;
}

export class PhoneCalendarOrderPaymentHelper extends CalendarOrderPaymentHelper {
  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof PhoneCalendarOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is PhoneCalendarOrderPaymentHelper {
    return dm instanceof PhoneCalendarOrderPaymentHelper;
  }

  public readonly orderTypeId: string = PhoneCalendarOrder.TYPE_ID;
}